import classNames from 'classnames';
import React, { HTMLAttributes, Suspense } from 'react';
import {
  Column,
  TableOptions,
  useFilters,
  useSortBy,
  useTable,
} from 'react-table';
import cssExports from './Table.module.css';
import { SortState } from 'toolbox/src/hooks/useSortedTable';

export type FixedColumnType<Data extends { [index: string]: any }> = Omit<
  Column<Data>,
  'accessor'
> & {
  accessor: string | ((data: Data, index: number) => any);
  CustomHeader?: (any) => React.ReactNode;
};

interface TableProps<Data> {
  columns: FixedColumnType<Data>[];
  data: Data[];
  sortingState?: SortState;
  onColumnClick?: (columnId: string) => void;
  /**
   * A list of columns you'd like to prevent the user sorting on.
   * Only relevant if you're using sortingState or onColumnClick
   */
  disabledSortColumns?: string[];
  /**
   * Customize the grid template columns, using the CSS
   * grid-template-columns syntax
   */
  gridTemplateColumns?: string;
  /**
   * Render an expandable row below each row of the table. It's
   * up to you to control the state, or whether this should return null
   */
  renderExpandableRow?: React.FC<{ row: Data; index: number }>;

  summaryRow?: React.ReactNode;
  highlightRow?: number | 'none-open';

  autoResetFilters?: boolean;
  manualFilters?: boolean;
}

function Table<Data extends object = {}>({
  columns,
  data,
  sortingState,
  onColumnClick,
  gridTemplateColumns = `repeat(${columns.length}, 1fr)`,
  renderExpandableRow: ExpandableRow,
  summaryRow = null,
  highlightRow = 'none-open',
  disabledSortColumns = [],
}: TableProps<Data>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns: columns as any,
      data,
    } as TableOptions<{}>,
    useFilters,
    useSortBy,
  );

  return (
    <Suspense fallback={null}>
      <div className={cssExports.Table}>
        <div className="inline-block min-w-full border border-gray-400 rounded-lg shadow-sm">
          <div
            {...getTableProps()}
            className={classNames('text-gray-700', cssExports.table)}
          >
            <div className={cssExports.thead}>
              {headerGroups.map((headerGroup, i) => (
                <div
                  {...headerGroup.getHeaderGroupProps()}
                  className={cssExports.tr}
                  style={{
                    gridTemplateColumns,
                  }}
                  key={i}
                >
                  {headerGroup.headers.map((column: any, i) => {
                    const columnIsBeingSorted =
                      column.id === sortingState?.columnId;

                    const canSort =
                      onColumnClick && !disabledSortColumns.includes(column.id);

                    return (
                      <div
                        {...column.getHeaderProps({
                          onClick: () => {
                            if (canSort) {
                              onColumnClick?.(column.id);
                            }
                          },
                        })}
                        key={i}
                        className={classNames(
                          columnIsBeingSorted
                            ? sortingState?.order === 'desc'
                              ? cssExports.SortedDesc
                              : cssExports.SortedAsc
                            : '',
                          canSort && cssExports.CanSort,
                          cssExports.th,
                        )}
                      >
                        {column.render('Header')}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div {...getTableBodyProps()} className={cssExports.tbody}>
              {rows.map(
                (row, i) =>
                  prepareRow(row) === null || (
                    <div
                      key={i}
                      className={classNames(
                        ExpandableRow && i === highlightRow
                          ? 'bg-jcap-100 shadow-inner'
                          : 'bg-white',
                        i >= rows.length - 1 && !summaryRow && 'rounded-b-lg',
                      )}
                    >
                      <TableRow
                        {...row.getRowProps()}
                        expandableRow={!!ExpandableRow}
                        gridTemplateColumns={gridTemplateColumns}
                      >
                        {row.cells.map((cell: any, i) => {
                          return (
                            <TableCell {...cell.getCellProps()} key={i}>
                              {cell.render('Cell')}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      {ExpandableRow ? (
                        <ExpandableRow row={data[i]} index={i} />
                      ) : null}
                    </div>
                  ),
              )}
              {summaryRow}
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  );
}

export default Table;

export const TableRow: React.FC<HTMLAttributes<HTMLDivElement> & {
  /**
   * Customize the grid template columns, using the CSS
   * grid-template-columns syntax
   */
  gridTemplateColumns?: string;
  expandableRow?: boolean;
}> = ({ gridTemplateColumns, expandableRow, ...props }) => {
  return (
    <div
      {...props}
      className={classNames(
        cssExports.tr,
        props.className,
        expandableRow && 'py-2',
      )}
      style={{ gridTemplateColumns }}
    />
  );
};

export const TableCell: React.FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  return (
    <div {...props} className={classNames(cssExports.td, props.className)} />
  );
};
